import React, { useMemo } from "react";
import { gregorianToHijri, formatHijriDate } from "./hijri";
import { cn } from "./utils";

interface DualDateDisplayProps {
  /** Date to display (Gregorian) */
  date: Date | string | null;
  /** Locale for display: 'en' for English, 'ar' for Arabic */
  locale?: "en" | "ar";
  /** Whether to show Hijri date (default: true) */
  showHijri?: boolean;
  /** Optional CSS class for the container */
  className?: string;
}

/**
 * Dual Date Display Component
 *
 * Displays both Gregorian and Hijri calendar dates for KSA compliance.
 * - EN locale: "9 May 2026 / 11 Dhul Qadah 1447"
 * - AR locale: "٩ مايو ٢٠٢٦ / ١١ ذو القعدة ١٤٤٧" (RTL)
 */
export const DualDateDisplay = React.forwardRef<HTMLDivElement, DualDateDisplayProps>(
  ({ date, locale = "en", showHijri = true, className }, ref) => {
    const { gregorian, hijri } = useMemo(() => {
      if (!date) {
        return { gregorian: null, hijri: null };
      }

      const d = typeof date === "string" ? new Date(date) : date;
      if (isNaN(d.getTime())) {
        return { gregorian: null, hijri: null };
      }

      const hijriResult = gregorianToHijri(d);
      return {
        gregorian: d,
        hijri: hijriResult,
      };
    }, [date]);

    if (!gregorian) {
      return (
        <div
          ref={ref}
          className={cn("text-sm text-slate-500", className)}
          dir={locale === "ar" ? "rtl" : "ltr"}
        >
          —
        </div>
      );
    }

    const gregorianFormatted = locale === "ar"
      ? gregorian.toLocaleDateString("ar-SA", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : gregorian.toLocaleDateString("en-GB", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });

    const hijriFormatted = hijri ? formatHijriDate(hijri, locale) : "";

    return (
      <div
        ref={ref}
        className={cn("space-y-1", className)}
        dir={locale === "ar" ? "rtl" : "ltr"}
      >
        <div className="text-sm font-medium">
          {gregorianFormatted}
        </div>
        {showHijri && hijriFormatted && (
          <div className="text-xs text-slate-500">
            {hijriFormatted}
          </div>
        )}
      </div>
    );
  }
);

DualDateDisplay.displayName = "DualDateDisplay";
